import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, AlertCircle } from "lucide-react";

const MEDIUM_USER = "nathenmazri";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIUM_FEED_API = `${BACKEND_URL}/api/medium-feed?user=${MEDIUM_USER}`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

function Eyebrow({ children, color = "#0041E6" }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase" style={{ color }}>
      <span className="inline-block w-6 h-px" style={{ background: color }} />
      {children}
    </div>
  );
}

function extractThumb(item) {
  if (item.thumbnail && item.thumbnail.startsWith("http")) return item.thumbnail;
  const match = (item.description || "").match(/<img[^>]+src="([^">]+)"/);
  return match ? match[1] : null;
}

function cleanExcerpt(html, max = 180) {
  const text = (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function NotesPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(MEDIUM_FEED_API);
        const data = await res.json();
        if (cancelled) return;
        if (data.status === "ok" && Array.isArray(data.items)) {
          setItems(data.items);
          setStatus("ok");
        } else {
          setStatus("error");
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="notes-page" className="bg-white">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-12 md:pb-16 bg-white">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#0041E6]/8 blur-3xl" />
        </div>
        <div className="mx-auto max-w-[1300px] px-5 md:px-12">
          <motion.div {...fadeUp}>
            <Eyebrow>Notes</Eyebrow>
          </motion.div>
          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <motion.h1
              {...fadeUp}
              className="font-display text-black leading-[0.9]"
              style={{ fontSize: "clamp(44px, 7vw, 104px)" }}
            >
              Thoughts from<br />
              <span className="italic text-[#0041E6]">the founder.</span>
            </motion.h1>
            <motion.a
              {...fadeUp}
              transition={{ delay: 0.1 }}
              href={`https://medium.com/@${MEDIUM_USER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-black/70 hover:text-[#0041E6] text-[15px] font-semibold"
              data-testid="medium-profile-link"
            >
              Follow on Medium <ArrowUpRight size={16} />
            </motion.a>
          </div>
          <motion.p
            {...fadeUp}
            transition={{ delay: 0.1 }}
            className="mt-6 text-[17px] md:text-[19px] text-black/70 max-w-2xl leading-relaxed"
          >
            Unfiltered notes on licensing, fandom, and building eGeez — published straight from the source.
          </motion.p>
        </div>
      </section>

      {/* ARTICLES GRID */}
      <section className="py-12 md:py-20 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1300px] px-5 md:px-12">
          {status === "loading" && (
            <div className="flex flex-col items-center justify-center py-24 text-black/50" data-testid="notes-loading">
              <Loader2 size={28} className="animate-spin" />
              <div className="mt-4 text-sm font-semibold tracking-wider uppercase">Loading latest notes…</div>
            </div>
          )}

          {status === "error" && (
            <div className="max-w-xl mx-auto text-center py-20" data-testid="notes-error">
              <AlertCircle size={28} className="mx-auto text-black/50" />
              <div className="mt-4 font-display text-[22px] text-black">Couldn't fetch the feed.</div>
              <p className="mt-2 text-[15px] text-black/60">
                Read the latest articles directly on{" "}
                <a
                  href={`https://medium.com/@${MEDIUM_USER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0041E6] font-semibold underline"
                >
                  Medium
                </a>.
              </p>
            </div>
          )}

          {status === "ok" && items.length === 0 && (
            <div className="text-center py-20 text-black/60">
              No articles published yet — check back soon.
            </div>
          )}

          {status === "ok" && items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="notes-grid">
              {items.map((item, i) => {
                const thumb = extractThumb(item);
                return (
                  <motion.a
                    key={item.guid || item.link || i}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="group rounded-[24px] bg-white border border-black/8 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    data-testid={`note-card-${i}`}
                  >
                    <div className="aspect-[16/9] bg-[#F1F1F4] overflow-hidden">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0041E6]/10 to-[#FFD93D]/10">
                          <span className="font-display text-[#0041E6] text-[42px]">eGeez</span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#0041E6]">
                        {formatDate(item.pubDate)}
                      </div>
                      <h3 className="mt-3 font-display text-black text-[22px] leading-tight line-clamp-3 group-hover:text-[#0041E6] transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-[14px] text-black/65 leading-relaxed line-clamp-3">
                        {cleanExcerpt(item.description)}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-2 text-[#0041E6] font-semibold text-[14px]">
                        Read on Medium <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </div>
                    </div>
                  </motion.a>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
